import React, { useEffect, useState } from "react";
import { useDataContext } from "../contexts/AuthContext";
import { getAllCategories, getSketchByOrderItemId } from "../services/api";
import { resolveFileUrl } from "../utils/media";
import logoPng from "../images/Frame.png";

/**
 * Vista de PRENDAS para el USUARIO NORMAL (read-only): lo único que ve es el boceto
 * vigente de cada prenda (si hay) y su estado. No edita guía ni talles (eso vive en
 * sus propias secciones / lo manejan tutor/delegado y el backoffice).
 *
 * Estado del boceto:
 *  - Pendiente  → todavía no hay boceto vigente.
 *  - Completado → hay boceto, falta aprobación.
 *  - Aprobado   → boceto aprobado.
 */
const estadoBoceto = (sketch) => {
  const tiene = !!(sketch && sketch.filename);
  if (!tiene) return { label: "Pendiente", bg: "#fee2e2", fg: "#b91c1c" };
  if (sketch.approved_at) return { label: "Aprobado", bg: "#dcfce7", fg: "#15803d" };
  // Render del propio cliente (guía): el boceto final lo arma la empresa → "En revisión",
  // no "Completado/falta aprobación" (el cliente no aprueba su propio diseño).
  if (sketch.uploaded_by_type !== "App\\Models\\User") return { label: "En revisión", bg: "#ffedd5", fg: "#9a3412" };
  return { label: "Completado", bg: "#fef3c7", fg: "#b45309" };
};

const C = {
  page: { padding: "1.1em 1em 2em", maxWidth: 760, margin: "0 auto" },
  h1: { margin: "0 0 .15em", fontFamily: '"Vinila Extended Bold", sans-serif', fontSize: "1.5em", color: "#0f172a" },
  sub: { margin: "0 0 1em", color: "#64748b", fontSize: ".95em" },
  card: { background: "#fff", border: "1px solid #e9eef5", borderRadius: "1em", padding: "1em", marginBottom: ".8em", boxShadow: "0 1px 3px rgba(0,0,0,.06)" },
  head: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: ".6em", marginBottom: ".7em" },
  name: { fontWeight: 800, color: "#0f172a", fontSize: "1.02em" },
  badge: (bg, fg) => ({ background: bg, color: fg, borderRadius: 999, padding: ".2em .7em", fontSize: ".75em", fontWeight: 800, whiteSpace: "nowrap" }),
  imgWrap: { textAlign: "center" },
  img: { maxWidth: "100%", maxHeight: 320, borderRadius: ".6em" },
  empty: { display: "flex", flexDirection: "column", alignItems: "center", gap: ".4em", color: "#94a3b8", padding: "1.4em 0", fontSize: ".9em" },
};

export default function PrendasBocetos() {
  const { contextData } = useDataContext();
  const { customer } = contextData;
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (!customer?.id || !customer?.order_id) return;
    (async () => {
      try {
        const base = await getAllCategories(customer.id, customer.order_id);
        const lista = Array.isArray(base) ? base : [];
        const conBoceto = await Promise.all(
          lista.map(async (p) => {
            let sketch = {};
            try {
              sketch = await getSketchByOrderItemId(p.order_item_id);
            } catch (e) { /* sin boceto */ }
            return { ...p, sketch: sketch || {} };
          })
        );
        setItems(conBoceto);
      } catch (error) {
        console.error("Error cargando bocetos:", error);
        setItems([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [customer?.id, customer?.order_id]);

  if (loading) {
    return (
      <div className="contcargando">
        <img src={logoPng} alt="littleBlueLogo" className="imgcargando" />
        <h2 className="text-muted">CARGANDO...</h2>
        <div className="loader"></div>
      </div>
    );
  }

  return (
    <div style={C.page}>
      <h1 style={C.h1}>Diseño</h1>
      <p style={C.sub}>Este es el boceto actual de cada prenda y su estado.</p>

      {items.length === 0 ? (
        <p style={C.sub}>No hay prendas en tu pedido.</p>
      ) : (
        items.map((p) => {
          const st = estadoBoceto(p.sketch);
          const tiene = !!(p.sketch && p.sketch.filename);
          return (
            <div key={p.order_item_id} style={C.card}>
              <div style={C.head}>
                <span style={C.name}>{p.product_name}{p.alias ? ` ${p.alias}` : ""}</span>
                <span style={C.badge(st.bg, st.fg)}>{st.label}</span>
              </div>
              {tiene ? (
                <div style={C.imgWrap}>
                  <img src={resolveFileUrl(p.sketch.filename)} alt={`Boceto de ${p.product_name}`} style={C.img} />
                </div>
              ) : (
                <div style={C.empty}>
                  <i className="bi bi-image" style={{ fontSize: "1.8em" }} />
                  <span>Todavía no hay un boceto para esta prenda.</span>
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}
